import { ReactNode, memo, useContext } from 'react';
import { Handle, Position } from 'reactflow';

import { DragOverlay, useDraggable } from '@dnd-kit/core';
import styled, { CSSProperties } from 'styled-components';

import {
  ActionCard,
  ActionCardContent,
  ActionCardDescription,
  ActionCardDescriptionProps,
  ActionCardHeader,
  ActionCardHeaderProps,
  ActionCardProps,
  DESCRIPTION_LINE_COUNT_LIMIT,
} from '@components/action-card';
import { Badge } from '@components/badge';
import { IconButton } from '@components/icon-button';
import { IconDrag } from '@components/icons/drag';
import { IconDropRight } from '@components/icons/dropright';
import { useIsSidebarActive } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { MakePropertiesOptional, Rename } from '@utils/types';

import { BASE_ACTION_TYPE } from 'api/types';
import { ActionIcon } from 'ui/actions/action-config';
import { TestIdContext } from 'ui/authenticated/main/helpers';
import { showTemplateActionSidebar } from 'ui/journey-template/action-sidebar';
import { isInTemplateRoute } from 'ui/router';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { JourneyNode } from './types';

import { MoveContext } from '../edge';
import { useCollapseContext } from '../hooks/use-collapsed-nodes';
import { ActionPresenceIndicator } from '../presence/action-presence-indicator';
import { usePresence } from '../presence/presence-context';
import { showActionSidebar, showPreviewActionSidebar } from '../show-action-sidebar';

const dragHandleWidth = 32;

const dragPlaceholderSize = pxToRem(64);

const overlayContainerClass = 'drag-overlay';
const dragHandleClassName = 'drag-handle';

export const StyledHandle = styled(Handle)<{ $color?: Color }>(({ $color }) => ({
  backgroundColor: theme.color.backgroundLightDark,
  transition: 'background-color 0.2s ease-out',
  borderRadius: '50%',
  width: pxToRem(12),
  height: pxToRem(12),
  border: `${pxToRem(2)} solid ${theme.color.backgroundLightDark}`,
  '&[data-handlepos="bottom"]': {
    bottom: 0,
  },
  '&[data-handlepos="top"]': {
    top: 0,
  },
  '> div': {
    height: '100%',
    width: '100%',
    borderRadius: '50%',
    backgroundColor: $color ? theme.color[$color] : theme.color.uiTrans20,
  },
}));

type NodeContainerProps = {
  node: JourneyNode;
  hideTargetHandle?: boolean;
  hideSourceHandle?: boolean;
  skipSelect?: boolean;
  disableClick?: boolean;
  id?: string;
};

export const NodeContainer: React.FC<NodeContainerProps> = ({
  children,
  node,
  hideTargetHandle = false,
  hideSourceHandle = false,
  skipSelect = false,
  disableClick = false,
  ...rest
}) => {
  const isActive = useIsSidebarActive(node.id);
  const { availableMoves, isMoveDisabled, isPreviewMode } = useContext(MoveContext);
  const descriptiveMap = useContext(TestIdContext);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: node.id,
    data: node,
  });

  const isDraggableNode = Boolean(availableMoves[node.id]) && !isPreviewMode;
  const isValidDrag = node && isDragging && !isMoveDisabled && !isPreviewMode;
  const handleColor = isActive ? 'elementBlue100' : undefined;

  const handleActionClick = (selectedNode: JourneyNode) => {
    if (isPreviewMode) {
      showPreviewActionSidebar(selectedNode);
      return;
    }
    const showSidebar = isInTemplateRoute() ? showTemplateActionSidebar : showActionSidebar;
    showSidebar(selectedNode);
  };

  return (
    <>
      <StyledNodeContainer
        data-testid={descriptiveMap[node.id]}
        $disableClick={disableClick}
        {...rest}
        onClick={() => handleActionClick(node)}
        onContextMenu={(e) => e.preventDefault()}
      >
        <DragContainer $isDragging={isDragging}>
          {isDraggableNode && (
            <DragHandle ref={setNodeRef} {...listeners} {...attributes}>
              <IconDrag color="elementPurple60" />
            </DragHandle>
          )}
          {node.type === 'journey_branch' && <CollapseHandler node={node} />}
          {children}
          <ActionPresenceIndicator id={node.id} />
          {isValidDrag && (
            <DragOverlay
              className={overlayContainerClass}
              style={{ top: 0, left: -dragHandleWidth, position: 'absolute' }}
            >
              <DragNode>
                <ActionIcon action={node.type} />
              </DragNode>
            </DragOverlay>
          )}
          {!hideTargetHandle && (
            <TargetHandle $color={handleColor} style={{ opacity: 0 }}>
              <div />
            </TargetHandle>
          )}
          {!hideSourceHandle && (
            <SourceHandle $color={handleColor} style={{ opacity: 0 }}>
              <div />
            </SourceHandle>
          )}
        </DragContainer>
      </StyledNodeContainer>
    </>
  );
};

type CollapseHandlerProps = {
  node: JourneyNode;
};

const CollapseHandler = ({ node }: CollapseHandlerProps) => {
  const { collapseAction: collapseNode, expandAction: expandNode } = useCollapseContext();

  const isCollapsed = Boolean(node.data?.isCollapsed);
  const toggleCollapsed = isCollapsed ? expandNode : collapseNode;

  return (
    <CollapseContainer>
      <CollapseButton
        $isCollapsed={isCollapsed}
        isRotated={!isCollapsed}
        degrees={90}
        icon={
          <>
            <IconDropRight color="purple40" size={16} />
            {isCollapsed && <Text text={node.data.descendantCount} font="regular12" />}
          </>
        }
        onClick={() => {
          toggleCollapsed(node.id);
        }}
      />
    </CollapseContainer>
  );
};

const CollapseContainer = styled.div({
  position: 'absolute',
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  zIndex: 1,
});

const CollapseButton = styled(IconButton).attrs({
  size: 'sm',
  variant: 'white-purple',
  stopPropagation: true,
  preventDefault: true,
})<{ $isCollapsed: boolean }>(({ $isCollapsed }) => ({
  borderWidth: pxToRem(1),
  borderColor: theme.color.border20,
  borderStyle: 'solid',
  position: 'absolute',
  svg: {
    position: 'absolute',
  },
  '&:hover': {
    boxShadow: theme.shadow.focus,
    '--shadow-focus-width': pxToRem(8),
    '--shadow-focus-gap': 0,
    '--shadow-focus-color': theme.color.uiTrans10,
    backgroundColor: theme.color.blue100,
    svg: {
      color: theme.color.white100,
    },
  },

  ...($isCollapsed && {
    '&&&:not(:hover)': {
      svg: {
        opacity: 0,
      },
    },
    '&&&:hover': {
      span: {
        position: 'absolute',
        opacity: 0,
      },
    },
  }),
}));

const StyledNodeContainer = styled.div<{ $disableClick?: boolean }>(({ $disableClick }) => ({
  width: '320px',
  display: 'flex',
  justifyContent: 'center',
  cursor: $disableClick ? 'default' : 'pointer',
}));

const DragContainer = styled.div<{ $isDragging?: boolean }>(({ $isDragging }) => ({
  position: 'relative',
  [`>*:not(.${overlayContainerClass}, .${dragHandleClassName})`]: {
    opacity: $isDragging ? 0.5 : 1,
  },
  [`&:hover .${dragHandleClassName}`]: {
    opacity: 1,
  },
}));

const DragHandle = styled.button.attrs({ className: dragHandleClassName })({
  position: 'absolute',
  width: pxToRem(dragHandleWidth),
  left: pxToRem(-dragHandleWidth),
  height: '100%',
  opacity: 0,
  transition: 'opacity 0.5s, transform 0.5s ease-in-out',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'grab',
  ':hover': {
    transform: 'scale(1.25)',
    svg: {
      color: theme.color.buttonIconPositive,
    },
  },
});

const DragNode: React.FC = (props) => {
  // React-flow scales with the zoom, so we need to find that factor and scale our overlay accordingly
  // First we find the flow scale (the zoom level)
  const flowContainer = document.getElementsByClassName('react-flow__container')[0] as HTMLElement;
  const flowTransform = flowContainer?.style.transform;
  const flowMatch = flowTransform?.match(/scale\(([^)]+)\)/);
  const flowScale = parseFloat(flowMatch?.[1] ?? '');

  // Then we find what our cursor has moved, which is what the overlay translates
  const overlayContainer = document.getElementsByClassName(overlayContainerClass)[0] as HTMLElement;
  const overlayTransform = overlayContainer?.style.transform;
  const overlayMatch = overlayTransform?.match(/translate3d\(([^)]+)\)/);

  const multiplyNumbersInString = (inputString: string = '', factor: number) =>
    inputString.replace(/-?\d+(\.\d+)?/g, (match) => {
      const number = parseFloat(match);
      return !isNaN(number) ? (number * factor).toString() : match;
    });

  // Then we need to invertly scale those values based on the flow scale
  // and also remember that the parent container has already been moved, therefore we subtract 1
  const scaleFactor = 1 / flowScale - 1;
  const scaled = multiplyNumbersInString(overlayMatch?.[1], scaleFactor);

  return <DragPlaceholder style={{ transform: `translate3d(${scaled})` }} {...props} />;
};

const DragPlaceholder = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: dragPlaceholderSize,
  width: dragPlaceholderSize,
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(8),
  borderColor: theme.color.border20,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  boxShadow: theme.shadow.low,
  cursor: 'grabbing',
  // Change color on the icons that are the same color as the background
  'svg[style*="--icon-color: var(--color-white-100)"]': {
    color: theme.color.teal100,
  },
});

const styling = {
  zIndex: 1,
  opacity: 0,
  height: 0,
  width: 0,
};

export const TargetHandle = styled(StyledHandle).attrs({
  type: 'target',
  position: Position.Top,
  id: 'target-handle',
})(styling);

export const SourceHandle = styled(StyledHandle).attrs({
  type: 'source',
  position: Position.Bottom,
  id: 'source-handle',
})(styling);

const getIsTraveller = (node: JourneyNode | TravellerJourneyNode): node is TravellerJourneyNode => {
  return Boolean('travellerJourneyId' in (node.data as TravellerJourneyNode['data']));
};

const getActionCardBackgroundColor = (
  node: JourneyNode | TravellerJourneyNode,
  overwriteError: boolean = false
): Color => {
  const defaultColor: Color = 'backgroundLightDark';
  const isTraveller = getIsTraveller(node);
  if (!isTraveller) {
    return defaultColor;
  }
  if (node.data.isHalted && node.data.isCompleted) {
    return 'greenTrans20';
  }
  if (
    node.data.isHalted ||
    node.data.isSkipped ||
    (node.data.isCompleted && node.type === 'placeholder')
  ) {
    return 'uiTrans10';
  }
  if (node.data.isError || overwriteError) {
    return 'redTrans20';
  }
  if (node.data.isCompleted) {
    return 'greenTrans20';
  }
  if (node.data.isInProgress) {
    return 'orangeTrans20';
  }
  if (!node.data.isAutomatic && node.data.parentCompleted) {
    return 'indigoTrans20';
  }
  return defaultColor;
};

type JourneyNodeActionCardProps = Omit<ActionCardProps, 'active' | 'children'> & {
  children?: ReactNode;
  renderBelowDescription?: ReactNode;
  isError?: boolean;
  node: JourneyNode<BASE_ACTION_TYPE>;
  isColorful?: boolean;
} & MakePropertiesOptional<Pick<ActionCardHeaderProps, 'label' | 'icon'>, 'icon'> &
  Rename<ActionCardDescriptionProps, { text: 'descriptionText' }>;

export const JourneyNodeActionCard = memo(
  ({
    node,
    children,
    label,
    descriptionText,
    isConfigured,
    tooltip,
    renderBelowDescription,
    icon,
    isError = false,
    ...rest
  }: JourneyNodeActionCardProps) => {
    const isActive = useIsSidebarActive(node.id);
    const backgroundColor = getActionCardBackgroundColor(node, isError);
    const { actionEditors, currentUserId } = usePresence();

    const editorUserIds = actionEditors.get(node.id);
    const otherEditors = editorUserIds
      ? Array.from(editorUserIds).filter((userId) => userId !== currentUserId)
      : [];
    const hasOtherEditors = otherEditors.length > 0;
    const borderColor = hasOtherEditors ? 'elementIndigo100' : undefined;

    return (
      <ActionCard
        active={isActive}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        {...rest}
      >
        <ActionCardHeader icon={icon ?? <ActionIcon action={node.type} />} label={label}>
          {children}
        </ActionCardHeader>
        <ActionCardContent>
          <ActionCardDescription
            text={descriptionText ?? node.data?.nodeDescription ?? node.data?.description}
            isConfigured={isConfigured ?? node.data?.isConfigured}
            tooltip={tooltip}
          />
          {renderBelowDescription}
        </ActionCardContent>
      </ActionCard>
    );
  }
);

type MoreItemsProps = {
  items?: any[];
  itemsMaxCount?: number;
};

export const MoreItems = ({
  items = [],
  itemsMaxCount = DESCRIPTION_LINE_COUNT_LIMIT,
}: MoreItemsProps) => {
  const { t } = useScopedTranslate(locale);
  const hasManyConditions = items.length > itemsMaxCount;

  if (!hasManyConditions) {
    return null;
  }

  return (
    <Text
      text={t('more', `${items.length - itemsMaxCount + 1}`)}
      font="regular14"
      color="textHelper"
    />
  );
};

type TravellerNodeActionCardProps = Omit<JourneyNodeActionCardProps, 'node'> & {
  node: TravellerJourneyNode<BASE_ACTION_TYPE>;
  cardStyles?: CSSProperties;
};

export const TravellerNodeActionCard = ({
  node,
  children,
  label,
  descriptionText,
  isConfigured,
  tooltip,
  renderBelowDescription,
  icon,
  isError = false,
  cardStyles,
  ...rest
}: TravellerNodeActionCardProps) => {
  const isActive = useIsSidebarActive(node.id);
  const backgroundColor = getActionCardBackgroundColor(node, isError);
  const { t } = useScopedTranslate(locale);
  return (
    <ActionCard active={isActive} backgroundColor={backgroundColor} {...rest}>
      <ActionCardHeader icon={icon ?? <ActionIcon action={node.type} />} label={label}>
        {children}
      </ActionCardHeader>
      <ActionCardContent style={cardStyles}>
        <ActionCardDescription
          text={descriptionText ?? node.data?.nodeDescription ?? node.data?.description}
          isConfigured={isConfigured ?? node.data?.isConfigured}
          tooltip={tooltip}
        />
        {renderBelowDescription}
        {node.data.isSkipped && (
          <Stack orientation="horizontal" style={{ paddingTop: pxToRem(8) }}>
            <Badge
              variant="neutral"
              size="md"
              text={node.data.hasFlowReached ? t('skipped') : t('skipAction')}
            />
          </Stack>
        )}
      </ActionCardContent>
    </ActionCard>
  );
};

const locale = {
  en: {
    more: '{0} more ..',
    skipAction: 'Skip action',
    skipped: 'Skipped',
  },
};
